import { Box, Stack, Typography, useTheme } from "@mui/material";
import { capitalizeText } from "helpers/methods";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { MobileSignRequestStyles } from "../Mobile/MobileSignRequest.styles";
import MobileSignatureIcon from "../../../assets/icons/MobileFieldIcons/MobileSignature.svg";
import MobileInitialsIcon from "../../../assets/icons/MobileFieldIcons/MobileInitials.svg";
import MobileTextIcon from "../../../assets/icons/MobileFieldIcons/MobileText.svg";
import MobileCheckboxIcon from "../../../assets/icons/MobileFieldIcons/MobileCheckbox.svg";
import {
  addSigner,
  ANY_SIGNER,
  ANY_SIGNER_COLOR,
  fieldColor,
  Fields,
  getFieldSize,
  getFieldSizeMobile,
  getNextAvailableId,
  getNextRadioGroupId,
  customFieldDefaults,
  customFieldKey,
  findCustomFieldByKey,
  libraryFieldProps,
  PDF_VIEWER_PAGE_SELECTOR,
} from "../SignRequestHelper";
import useCustomFieldLibrary from "./useCustomFieldLibrary";
import MobileCustomFieldsSheet from "../Mobile/components/MobileCustomFieldsSheet";
import AddRounded from "@mui/icons-material/AddRounded";
import { pressableProps } from "../Mobile/mobileA11y";
import { CustomField, FileField } from "models/interfaces";
import Field from "./Field";
import strings from "global/constants/StringConstants";
import { getBoundingClientRect, UseDocument } from "./UseDocument";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectDefaultDateFormat } from "redux/preferencesSlice";
import {
  setReduxSelectedFile,
  setReduxSelectedParticipant,
  updateFileInSignRequest,
} from "redux/appSlice";
import { RootState } from "utils/store";

// Figma mobile field-option icons (differ from the desktop field icons).
const MOBILE_FIELD_ICONS: Record<string, string> = {
  [strings.signature]: MobileSignatureIcon,
  [strings.initials]: MobileInitialsIcon,
  [strings.text]: MobileTextIcon,
  [strings.checkbox]: MobileCheckboxIcon,
};

const FieldsPanelMobile = ({
  setIsFieldWithinBounds,
  setPreviewCoords,
  selectedField,
  setSelectedField,
  isMobile,
  chipVariant = "icon",
  containerSx,
}: {
  setIsFieldWithinBounds: (value: boolean) => void;
  setPreviewCoords: (value: { x: number; y: number }) => void;
  selectedField: string | null | undefined;
  setSelectedField: (value: string | null) => void;
  isMobile?: boolean;
  /** "labeled" renders icon+label chips for the redesigned mobile editor. */
  chipVariant?: "icon" | "labeled";
  /** Optional override for the outer container styling. */
  containerSx?: any;
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const m = theme.palette.mobile;
  const classes = MobileSignRequestStyles(theme);
  const { isWithinPageBounds, getPage } = UseDocument();

  const { selectedFile, selectedParticipant, signRequest } = useAppSelector(
    (state: RootState) => state.app,
  );
  const defaultDateFormat = useAppSelector(selectDefaultDateFormat);
  const { customFields } = useCustomFieldLibrary();
  // The mobile flow's "Custom fields" chip opens the library sheet (create /
  // edit / delete); the saved fields themselves are drag chips above it.
  const [customFieldsSheetOpen, setCustomFieldsSheetOpen] = useState(false);

  const [initialDragPosition, setInitialDragPosition] = useState({
    x: 0,
    y: 0,
  });

  const dragDistance = useRef(0);

  const fieldBounds = useRef({
    height: 0,
    width: 0,
  });

  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      const $page = getPage(event, PDF_VIEWER_PAGE_SELECTOR);
      const isWithin = isWithinPageBounds(
        event,
        PDF_VIEWER_PAGE_SELECTOR,
        $page,
        fieldBounds.current.width,
        fieldBounds.current.height,
      );

      setIsFieldWithinBounds(isWithin);

      const DRAG_PREVIEW_SIZE = 24;
      setPreviewCoords({
        x: event.clientX - DRAG_PREVIEW_SIZE / 2,
        y: event.clientY - DRAG_PREVIEW_SIZE / 2,
      });

      if (initialDragPosition.x && initialDragPosition.y) {
        const dx = event.clientX - initialDragPosition.x;
        const dy = event.clientY - initialDragPosition.y;
        dragDistance.current = Math.sqrt(dx * dx + dy * dy);
      }
    },
    [
      getPage,
      isWithinPageBounds,
      initialDragPosition,
      setIsFieldWithinBounds,
      setPreviewCoords,
    ],
  );

  const onMouseClick = useCallback(
    async (event: MouseEvent) => {
      if (!selectedField) {
        return;
      }

      const $page = getPage(event, PDF_VIEWER_PAGE_SELECTOR);

      if (
        !$page ||
        !isWithinPageBounds(
          event,
          PDF_VIEWER_PAGE_SELECTOR,
          $page,
          fieldBounds.current.width,
          fieldBounds.current.height,
        )
      ) {
        setSelectedField(null);
        setInitialDragPosition({ x: 0, y: 0 });
        return;
      }

      const pageNumber = parseInt(
        $page.getAttribute("data-page-number") ?? "1",
        10,
      );

      const { top, left, height, width } = getBoundingClientRect($page);

      const placementIndicator = document.createElement("div");
      const pageX = event.clientX - left;
      const pageY = event.clientY - top;

      placementIndicator.style.position = "absolute";
      placementIndicator.style.top = `${pageY - fieldBounds.current.height / 2}px`;
      placementIndicator.style.left = `${pageX - fieldBounds.current.width / 2}px`;
      $page.appendChild(placementIndicator);

      // Use clientX/clientY which are viewport-relative to match getBoundingClientRect
      let fieldPageX = ((event.clientX - left) / width) * 100;
      let fieldPageY = ((event.clientY - top) / height) * 100;

      // A drag from the Custom Fields strip carries `custom:<id>`; the field is
      // placed as the definition's type with its saved config copied in.
      const definition = findCustomFieldByKey(selectedField, customFields);
      const fieldType = definition?.fieldType ?? selectedField;
      const [fieldW, fieldH] = getFieldSize(fieldType);

      const fieldPageWidth = (fieldW / width) * 100;
      const fieldPageHeight = (fieldH / height) * 100;

      fieldPageX -= fieldPageWidth / 2;
      fieldPageY -= fieldPageHeight / 2;

      const isRadio = fieldType === strings.radioButton;
      const radioGroupId = isRadio
        ? getNextRadioGroupId(selectedFile)
        : undefined;
      const sameTypeCount = selectedFile.fields.filter(
        (f: FileField) =>
          f.type === fieldType && f.signerIndex === selectedParticipant.id,
      ).length;
      const labelSuffix = sameTypeCount + 1;

      const newField: any = {
        id: getNextAvailableId(selectedFile),
        label: isRadio
          ? `Option 1`
           : (() => {
              const baseName = selectedParticipant.name || selectedParticipant.requestRole || "Signer";
              const firstName = baseName.includes(" ") ? baseName.split(" ")[0] : baseName;
              return `${firstName}-${fieldType?.split(" ").join("_").toLowerCase()}-${labelSuffix}`;
            })(),
        type: fieldType,
        frameX: width,
        frameY: height,
        x: fieldPageX,
        y: fieldPageY,
        w: fieldPageWidth,
        h: fieldPageHeight,
        fieldPageX: fieldPageX,
        fieldPageY: fieldPageY,
        fieldPageWidth,
        fieldPageHeight,
        width: width,
        height: height,
        left: left,
        top: top,
        page: pageNumber - 1,
        color: fieldColor[selectedParticipant.id],
        signerIndex: selectedParticipant.id,
        maxLength: 200,
        required: fieldType !== strings.checkbox,
        ...(isRadio
          ? { groupId: radioGroupId, value: "false", required: true }
          : {}),
        ...(fieldType === strings.date ||
        fieldType === strings.dateSigned
          ? { dateFormat: defaultDateFormat }
          : {}),
        hidden: false,
        // readOnly: false,
        readOnly: fieldType === strings.email,
        value:
          fieldType === strings.email
            ? selectedParticipant.email || ""
            : undefined,
        tabLabel: "",
        toolTip: "",
        validationPattern: "none",
        validationMessage: "",
        placeholder: "",
        multiLine: false,
        styles: {
          fontFamily: "Times",
          color: "#1A1A1A",
          size: 16,
          fontWeight: {
            bold: false,
            italic: false,
            underline: false,
          },
        },
        existingField: false,
        // Only a Custom Fields drag carries the custom-field properties; a Fields-panel
        // drag keeps exactly the shape it had before.
        ...(definition
          ? {
              ...customFieldDefaults(fieldType),
              ...libraryFieldProps(definition, signRequest?.selfSigned === true),
            }
          : {}),
      };
      if (definition?.config?.signerIndex === ANY_SIGNER) {
        newField.signerIndex = ANY_SIGNER;
        newField.color = ANY_SIGNER_COLOR;
      }

      const updatedFile = {
        ...selectedFile,
        fields: [...selectedFile.fields, newField],
      };

      dispatch(setReduxSelectedFile(updatedFile));
      dispatch(updateFileInSignRequest(updatedFile));

      setIsFieldWithinBounds(false);
      setSelectedField(null);
      setInitialDragPosition({ x: 0, y: 0 });
    },
    [
      dispatch,
      isWithinPageBounds,
      selectedField,
      customFields,
      selectedFile,
      selectedParticipant,
      setIsFieldWithinBounds,
      setSelectedField,
      getPage,
    ],
  );

  const onTouchMove = useCallback(
    (event: TouchEvent) => {
      event.preventDefault(); // prevent scroll while dragging
      const touch = event.touches[0];

      // Use elementFromPoint to find the PDF page under the finger
      const elementUnder = document.elementFromPoint(
        touch.clientX,
        touch.clientY,
      );
      const $page = elementUnder?.closest(
        PDF_VIEWER_PAGE_SELECTOR,
      ) as HTMLElement | null;

      const mouseEvent = {
        clientX: touch.clientX,
        clientY: touch.clientY,
        target: elementUnder,
      } as unknown as MouseEvent;

      const isWithin = $page
        ? isWithinPageBounds(
            mouseEvent,
            PDF_VIEWER_PAGE_SELECTOR,
            $page,
            fieldBounds.current.width,
            fieldBounds.current.height,
          )
        : false;

      // console.log("$page", $page, "isWithin", isWithin); // debug

      setIsFieldWithinBounds(isWithin);
      const DRAG_PREVIEW_SIZE = 24;
      setPreviewCoords({
        x: touch.clientX - DRAG_PREVIEW_SIZE / 2,
        y: touch.clientY - DRAG_PREVIEW_SIZE / 2,
      });
    },
    [isWithinPageBounds, setIsFieldWithinBounds, setPreviewCoords],
  );

  // const onTouchEnd = useCallback(
  //   (event: TouchEvent) => {
  //     const touch = event.changedTouches[0];

  //     const elementUnder = document.elementFromPoint(touch.clientX, touch.clientY);
  //     const $page = elementUnder?.closest(PDF_VIEWER_PAGE_SELECTOR) as HTMLElement | null;

  //     if (!$page) {
  //       setSelectedField(null);
  //       setInitialDragPosition({ x: 0, y: 0 });
  //       return;
  //     }

  //     const syntheticEvent = {
  //       clientX: touch.clientX,
  //       clientY: touch.clientY,
  //       target: $page,
  //     } as unknown as MouseEvent;

  //     onMouseClick(syntheticEvent);
  //   },
  //   [onMouseClick, setSelectedField]
  // );

  const onTouchEnd = useCallback(
    (event: TouchEvent) => {
      const touch = event.changedTouches[0];

      const elementUnder = document.elementFromPoint(
        touch.clientX,
        touch.clientY,
      );
      const $page = elementUnder?.closest(
        PDF_VIEWER_PAGE_SELECTOR,
      ) as HTMLElement | null;

      if (!$page) {
        setSelectedField(null);
        setInitialDragPosition({ x: 0, y: 0 });
        return;
      }

      //  Use native getBoundingClientRect (viewport coords, no scrollY offset)
      const { top, left, height, width } = getBoundingClientRect($page);

      const pageNumber = parseInt(
        $page.getAttribute("data-page-number") ?? "1",
        10,
      );

      const naturalWidth = selectedFile.dimensions[pageNumber - 1]?.x ?? width;
      const naturalHeight =
        selectedFile.dimensions[pageNumber - 1]?.y ?? height;

      const definition = findCustomFieldByKey(selectedField, customFields);
      const fieldType: string = definition?.fieldType ?? selectedField ?? "";
      const [baseW, baseH] = getFieldSizeMobile(fieldType);

      const scaleX = width / naturalWidth;
      const scaleY = height / naturalHeight;

      const fieldW = baseW * scaleX;
      const fieldH = baseH * scaleY;

      const fieldPageWidth = (fieldW / width) * 100;
      const fieldPageHeight = (fieldH / height) * 100;

      let fieldPageX =
        ((touch.clientX - left) / width) * 100 - fieldPageWidth / 2;
      let fieldPageY =
        ((touch.clientY - top) / height) * 100 - fieldPageHeight / 2;

      // Clamp drop location so edge drops still place the field fully inside page.
      const maxX = Math.max(0, 100 - fieldPageWidth);
      const maxY = Math.max(0, 100 - fieldPageHeight);
      fieldPageX = Math.min(Math.max(fieldPageX, 0), maxX);
      fieldPageY = Math.min(Math.max(fieldPageY, 0), maxY);

      const isRadio = fieldType === strings.radioButton;
      const radioGroupId = isRadio
        ? getNextRadioGroupId(selectedFile)
        : undefined;
      const sameTypeCount = selectedFile.fields.filter(
        (f: FileField) =>
          f.type === fieldType && f.signerIndex === selectedParticipant.id,
      ).length;
      const labelSuffix = sameTypeCount + 1;

      const newField: any = {
        id: getNextAvailableId(selectedFile),
        label: isRadio
          ? `Option 1`
          : `${
              selectedParticipant.name.includes(" ")
                ? selectedParticipant.name.split(" ")[0]
                : selectedParticipant.name
            }-${fieldType?.split(" ").join("_").toLowerCase()}-${labelSuffix}`,
        type: fieldType,
        frameX: width,
        frameY: height,
        x: fieldPageX,
        y: fieldPageY,
        w: fieldPageWidth,
        h: fieldPageHeight,
        fieldPageX,
        fieldPageY,
        fieldPageWidth,
        fieldPageHeight,
        width,
        height,
        left,
        top,
        page: pageNumber - 1,
        color: fieldColor[selectedParticipant.id],
        signerIndex: selectedParticipant.id,
        maxLength: 200,
        required: fieldType !== strings.checkbox,
        ...(isRadio
          ? { groupId: radioGroupId, value: "false", required: true }
          : {}),
        ...(fieldType === strings.date ||
        fieldType === strings.dateSigned
          ? { dateFormat: defaultDateFormat }
          : {}),
        hidden: false,
        // readOnly: false,
        readOnly: fieldType === strings.email,
        value:
          fieldType === strings.email
            ? selectedParticipant.email || ""
            : undefined,
        tabLabel: "",
        toolTip: "",
        validationPattern: "none",
        validationMessage: "",
        placeholder: "",
        multiLine: false,
        styles: {
          fontFamily: "Times",
          color: "#1A1A1A",
          size: 16,
          fontWeight: { bold: false, italic: false, underline: false },
        },
        existingField: false,
        // Only a Custom Fields drag carries the custom-field properties; a Fields-panel
        // drag keeps exactly the shape it had before.
        ...(definition
          ? {
              ...customFieldDefaults(fieldType),
              ...libraryFieldProps(definition, signRequest?.selfSigned === true),
            }
          : {}),
      };
      if (definition?.config?.signerIndex === ANY_SIGNER) {
        newField.signerIndex = ANY_SIGNER;
        newField.color = ANY_SIGNER_COLOR;
      }

      const updatedFile = {
        ...selectedFile,
        fields: [...selectedFile.fields, newField],
      };

      dispatch(setReduxSelectedFile(updatedFile));
      dispatch(updateFileInSignRequest(updatedFile));

      setIsFieldWithinBounds(false);
      setSelectedField(null);
      setInitialDragPosition({ x: 0, y: 0 });
    },
    [
      selectedField,
      customFields,
      selectedFile,
      selectedParticipant,
      dispatch,
      setIsFieldWithinBounds,
      setSelectedField,
    ],
  );

  // const onTouchEnd = useCallback(
  // (event: TouchEvent) => {
  //   const touch = event.changedTouches[0];

  //   const elementUnder = document.elementFromPoint(touch.clientX, touch.clientY);
  //   const $page = elementUnder?.closest(
  //     PDF_VIEWER_PAGE_SELECTOR,
  //   ) as HTMLElement | null;

  //   if (!$page) {
  //     setSelectedField(null);
  //     setInitialDragPosition({ x: 0, y: 0 });
  //     return;
  //   }

  //   onMouseClick({
  //     clientX: touch.clientX + window.scrollX,
  //     clientY: touch.clientY + window.scrollY,
  //     target: elementUnder,
  //   } as unknown as MouseEvent);
  // },
  // [onMouseClick, setSelectedField],
  // );

  useEffect(() => {
    if (selectedField) {
      // window.addEventListener("mousemove", onMouseMove);
      // window.addEventListener("mouseup", onMouseClick);
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("touchend", onTouchEnd);
    }

    return () => {
      // window.removeEventListener("mousemove", onMouseMove);
      // window.removeEventListener("mouseup", onMouseClick);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [onMouseClick, onMouseMove, onTouchEnd, onTouchMove, selectedField]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const $page = document.querySelector(PDF_VIEWER_PAGE_SELECTOR);

      if (!$page) {
        return;
      }

      fieldBounds.current = {
        height: 36,
        width: 36,
      };
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (signRequest.signers.length === 0) {
      addSigner(selectedParticipant, signRequest.signers, dispatch);
    }
    if (!selectedParticipant || (!selectedParticipant.name && !selectedParticipant.requestRole && !selectedParticipant.email)) {
      dispatch(setReduxSelectedParticipant(undefined));
    }
  }, []);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes fieldDragStart {
        0% { transform: scale(1); }
        50% { transform: scale(0.9); }
        100% { transform: scale(1); }
      }
      .field-dragging {
        animation: fieldDragStart 0.3s ease-out;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fieldsConfig = Fields(theme) as any;
  // const allMobileFields = [
  //   ...(fieldsConfig?.Fields ?? []),
  //   ...((fieldsConfig?.["Personal Info"] ?? []) as any[]).filter(
  //     (f) => f.type === strings.name
  //   ),
  // ];

  return (
    <Box
      sx={{
        ...(isMobile
          ? {
              position: "sticky",
              bottom: 0,
              zIndex: 100,
              backgroundColor: "#ffffff",
              borderTop: "1px solid #E6E6E6",
              px: 0,
              pt: 1,
            }
          : { px: 2 }),
        ...containerSx,
      }}
    >
      <Stack
        display="flex"
        flexDirection="column"
        spacing={2}
        height="100%"
        width="100%"
      >
        <Stack
          id="mobile-draggable-fields"
          direction="row"
          flexWrap="nowrap"
          spacing={1}
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
            justifyContent: { xs: "flex-start", sm: "center" },
            whiteSpace: "nowrap",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {[
            ...Object.values(fieldsConfig).flat(),
            // Saved custom fields drag like any chip; the key resolves back
            // to the definition on drop.
            ...customFields.map((definition: CustomField) => ({
              type: customFieldKey(definition.id as string),
              iconType: definition.fieldType,
              name: definition.name,
              icon:
                MOBILE_FIELD_ICONS[definition.fieldType] ||
                (Object.values(fieldsConfig)
                  .flat()
                  .find((f: any) => f.type === definition.fieldType) as any)
                  ?.icon,
            })),
          ].map((field: any, ind: number) =>
              chipVariant === "labeled" ? (
                <Box
                  key={ind}
                  onMouseDown={(e) => {
                    setSelectedField(field.type);
                    setInitialDragPosition({ x: e.clientX, y: e.clientY });
                    dragDistance.current = 0;
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    const touch = e.touches[0];
                    setSelectedField(field.type);
                    setInitialDragPosition({
                      x: touch.clientX,
                      y: touch.clientY,
                    });
                    dragDistance.current = 0;
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flexShrink: 0,
                    cursor: "grab",
                    userSelect: "none",
                    bgcolor: m.surface.white,
                    border: `1px solid ${m.grey[50]}`,
                    borderRadius: "12px",
                    p: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      background: classes.primaryGradient50,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={
                        MOBILE_FIELD_ICONS[field.iconType ?? field.type] ||
                        field.icon
                      }
                      alt=""
                      width={16}
                      aria-hidden="true"
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: m.base[900],
                      whiteSpace: "nowrap",
                    }}
                  >
                    {field.name ??
                      capitalizeText(field.type.replace(/_/g, " "))}
                  </Typography>
                </Box>
              ) : (
                <Field
                  key={ind}
                  field={field}
                  dragDistance={dragDistance}
                  setDragStartPosition={setInitialDragPosition}
                  setSelectedField={setSelectedField}
                  showLabel={false}
                />
              ),
            )}
          {chipVariant === "labeled" && (
            <Box
              {...pressableProps(() => setCustomFieldsSheetOpen(true), {
                label: "Manage custom fields",
                hasPopup: "dialog",
              })}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
                cursor: "pointer",
                userSelect: "none",
                bgcolor: m.surface.white,
                border: `1px dashed ${m.ePurple[500]}`,
                borderRadius: "12px",
                p: 1,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  background: classes.primaryGradient50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AddRounded
                  aria-hidden="true"
                  sx={{ fontSize: 18, color: m.ePurple[500] }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: m.ePurple[500],
                  whiteSpace: "nowrap",
                }}
              >
                Custom fields
              </Typography>
            </Box>
          )}
        </Stack>
      </Stack>

      <MobileCustomFieldsSheet
        open={customFieldsSheetOpen}
        onClose={() => setCustomFieldsSheetOpen(false)}
      />
    </Box>
  );
};

export default FieldsPanelMobile;
